import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { Ban, CheckCircle2, Loader2, UserPlus } from 'lucide-react'

import { formatEmploymentFraction } from '@czas/shared'

import Alert from '@/components/ui/Alert'
import Input from '@/components/ui/Input'
import { apiErrorMessage } from '@/lib/api'
import { cn } from '@/lib/cn'
import { useCanManageAdmins } from '@/lib/acl'
import { useAdminUsers } from '@/features/admin/use-admin-users'
import { useAdminVacationBalances } from '@/features/admin/use-admin-vacation'

export default function UsersList() {
  const canManageAdmins = useCanManageAdmins()
  const [showInactive, setShowInactive] = useState(false)
  const [includeDeleted, setIncludeDeleted] = useState(false)
  const [search, setSearch] = useState('')

  const { data, isLoading, error } = useAdminUsers({
    active: showInactive ? 'all' : 'true',
    includeDeleted: canManageAdmins ? includeDeleted : false,
  })

  const currentYear = new Date().getFullYear()
  const balancesQuery = useAdminVacationBalances(currentYear)
  const balanceByUserId = useMemo(() => {
    const map = new Map<string, { totalDays: number; usedDays: number }>()
    for (const b of balancesQuery.data?.balances ?? []) {
      map.set(b.user.id, {
        totalDays: b.balance.totalDays,
        usedDays: b.balance.usedDays,
      })
    }
    return map
  }, [balancesQuery.data])

  const filtered = useMemo(() => {
    if (!data) return []
    const q = search.toLowerCase().trim()
    if (!q) return data.users
    return data.users.filter(
      (u) =>
        u.name.toLowerCase().includes(q) ||
        u.email.toLowerCase().includes(q) ||
        (u.position?.name.toLowerCase().includes(q) ?? false),
    )
  }, [data, search])

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <h1 className="text-2xl font-semibold">Pracownicy</h1>
        <Link
          to="/admin/users/new"
          className="inline-flex items-center gap-2 rounded-action px-4 py-2.5 text-sm font-medium bg-brand-dark text-bg-elevated hover:opacity-90 transition"
        >
          <UserPlus className="h-4 w-4" aria-hidden />
          Dodaj pracownika
        </Link>
      </div>

      <div className="flex items-center gap-3 flex-wrap">
        <Input
          type="search"
          placeholder="Szukaj po nazwisku lub email…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="max-w-xs"
        />
        <label className="flex items-center gap-2 text-sm text-text-muted cursor-pointer select-none">
          <input
            type="checkbox"
            checked={showInactive}
            onChange={(e) => setShowInactive(e.target.checked)}
            className="rounded border-border-strong"
          />
          Pokaż dezaktywowanych
        </label>
        {canManageAdmins && (
          <label className="flex items-center gap-2 text-sm text-text-muted cursor-pointer select-none">
            <input
              type="checkbox"
              checked={includeDeleted}
              onChange={(e) => setIncludeDeleted(e.target.checked)}
              className="rounded border-border-strong"
            />
            Pokaż usuniętych
          </label>
        )}
      </div>

      {error && <Alert variant="error">{apiErrorMessage(error)}</Alert>}

      <div className="rounded-card border border-border-subtle bg-bg-elevated overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-bg-surface text-text-muted text-xs uppercase tracking-wider">
              <tr>
                <th className="text-left px-4 py-3 font-medium">Imię</th>
                <th className="text-left px-4 py-3 font-medium">Email</th>
                <th className="text-left px-4 py-3 font-medium">Rola</th>
                <th className="text-left px-4 py-3 font-medium">Stanowisko</th>
                <th className="text-left px-4 py-3 font-medium">Etat</th>
                <th className="text-left px-4 py-3 font-medium">Urlop {currentYear}</th>
                <th className="text-left px-4 py-3 font-medium">Status</th>
                <th className="text-left px-4 py-3 font-medium">Akcje</th>
              </tr>
            </thead>
            <tbody>
              {isLoading && (
                <tr>
                  <td colSpan={8} className="px-4 py-8 text-center text-text-faint">
                    <Loader2 className="inline h-4 w-4 animate-spin mr-2" aria-hidden />
                    Ładuję pracowników…
                  </td>
                </tr>
              )}
              {!isLoading &&
                filtered.map((u) => (
                  <tr
                    key={u.id}
                    className={cn(
                      'border-t border-border-subtle hover:bg-bg-surface/50 transition',
                      !u.active && 'opacity-60',
                      u.deletedAt && 'opacity-60',
                    )}
                  >
                    <td className="px-4 py-3 whitespace-nowrap font-medium">
                      <span
                        className={cn(
                          u.deletedAt && 'line-through text-text-muted',
                        )}
                      >
                        {u.name}
                      </span>
                      {u.deletedAt && (
                        <span className="ml-2 text-xs text-text-faint">
                          (usunięty)
                        </span>
                      )}
                      {!u.hasPassword && !u.deletedAt && (
                        <span className="ml-2 inline-flex items-center text-[10px] font-medium text-warning bg-warning/10 px-2 py-0.5 rounded-pill">
                          Czeka na akceptację
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-text-muted">{u.email}</td>
                    <td className="px-4 py-3">
                      <span
                        className={cn(
                          'inline-flex rounded-pill px-2 py-0.5 text-xs',
                          u.role === 'ADMIN'
                            ? 'bg-brand-accent/15 text-brand-accent'
                            : 'bg-bg-surface text-text-muted',
                        )}
                      >
                        {u.role === 'ADMIN' ? 'Admin' : 'Pracownik'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-text-muted">
                      {u.position ? (
                        <span className="text-text-primary">{u.position.name}</span>
                      ) : (
                        <span className="text-text-faint">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm">
                      {u.employmentFraction != null ? (
                        <span className="font-mono tabular-nums">
                          {formatEmploymentFraction(u.employmentFraction)}
                        </span>
                      ) : (
                        <span className="text-text-faint">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm">
                      {(() => {
                        const b = balanceByUserId.get(u.id)
                        if (!b) return <span className="text-text-faint">—</span>
                        return (
                          <span>
                            <span className="font-medium text-text-primary">{b.usedDays}</span>
                            <span className="text-text-muted"> / {b.totalDays}</span>
                          </span>
                        )
                      })()}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      {u.active ? (
                        <span className="inline-flex items-center gap-1.5 text-success">
                          <CheckCircle2 className="h-3.5 w-3.5" aria-hidden />
                          Aktywny
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1.5 text-text-faint">
                          <Ban className="h-3.5 w-3.5" aria-hidden />
                          Dezaktywowany
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <Link
                        to={`/admin/users/${u.id}`}
                        className="text-brand-accent hover:underline"
                      >
                        Edytuj
                      </Link>
                    </td>
                  </tr>
                ))}
              {!isLoading && filtered.length === 0 && (
                <tr>
                  <td
                    colSpan={8}
                    className="px-4 py-8 text-center text-text-faint"
                  >
                    {search
                      ? 'Brak pracowników pasujących do wyszukiwania.'
                      : 'Brak pracowników do wyświetlenia.'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}
